import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Booking, getDatesInRange, monthNames } from '../utils/heatmapUtils';
import { HeatmapCell, HeatmapLegend, MonthRow } from './HeatmapComponents';

const YearlyBookingHeatmap = ({
    bookings,
    onDayClick,
}: {
    bookings: Booking[];
    onDayClick?: (monthDay: string, bookings: Booking[]) => void;
}) => {
    const bookingsByMonthDay: {
        [monthDay: string]: {
            count: number;
            years: Set<number>;
            bookings: Booking[];
        };
    } = {};

    for (const booking of bookings) {
        const dates = getDatesInRange(booking.checkin, booking.checkout);
        for (const date of dates) {
            const [year, month, day] = date.split('-');
            const monthDay = `${month}-${day}`;
            const yearNum = Number(year);

            if (!bookingsByMonthDay[monthDay]) {
                bookingsByMonthDay[monthDay] = {
                    count: 0,
                    years: new Set(),
                    bookings: [],
                };
            }
            const monthDayData = bookingsByMonthDay[monthDay];
            if (monthDayData) {
                monthDayData.count += 1;
                monthDayData.years.add(yearNum);
                if (!monthDayData.bookings.includes(booking)) {
                    monthDayData.bookings.push(booking);
                }
            }
        }
    }

    const maxCount = Math.max(
        ...Object.values(bookingsByMonthDay).map(v => v.count),
        1
    );

    const monthsData = monthNames.map((monthName, monthIndex) => {
        const month = String(monthIndex + 1).padStart(2, '0');
        const daysInMonth = new Date(
            Date.UTC(2024, monthIndex + 1, 0)
        ).getUTCDate();
        const days = [];

        for (let day = 1; day <= daysInMonth; day++) {
            const dayStr = String(day).padStart(2, '0');
            const monthDay = `${month}-${dayStr}`;
            const data = bookingsByMonthDay[monthDay] || {
                count: 0,
                years: new Set(),
                bookings: [],
            };
            const years = Array.from(data.years).sort((a, b) => b - a);

            days.push({
                day,
                monthDay,
                count: data.count,
                years,
                bookings: data.bookings,
            });
        }

        return { month: monthName, days };
    });

    return (
        <Box display="flex" flexDirection="column" gap={3}>
            <Typography variant="h5">
                All Years Combined - Booking Heatmap
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Shows total bookings for each day across all years
            </Typography>
            <Box display="flex" flexDirection="column" gap={2}>
                {monthsData.map(({ month, days }) => (
                    <MonthRow key={month} monthName={month}>
                        {days.map(
                            ({
                                monthDay,
                                count,
                                years,
                                bookings: dayBookings,
                            }) => (
                                <HeatmapCell
                                    key={monthDay}
                                    count={count}
                                    maxCount={maxCount}
                                    tooltipTitle={(() => {
                                        if (count === 0) {
                                            return `${monthDay}: No bookings`;
                                        }
                                        const bookingText =
                                            count === 1
                                                ? 'booking'
                                                : 'bookings';
                                        return `${monthDay}: ${count} ${bookingText} (${years.join(', ')})`;
                                    })()}
                                    size={20}
                                    onClick={() =>
                                        onDayClick?.(monthDay, dayBookings)
                                    }
                                />
                            )
                        )}
                    </MonthRow>
                ))}
            </Box>
            <HeatmapLegend maxCount={maxCount} size={20} />
        </Box>
    );
};

export default YearlyBookingHeatmap;
